import { useState, useEffect, useRef } from "react";
import toast from "../../../../utils/toast";
import {
  getSupportTicketData,
  createSupportTicket,
  getSupportTicketCategories,
  getSupportTicketById,
  getMessagesSupportTicketById,
  replyMessagesSupportTicket,
} from "../../../../services/tenant/tenantServices";

export default function SupportTicket() {
  const chatContainerRef = useRef(null);
  const [ticketData, setTicketData] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [ticketForm, setTicketForm] = useState({
    subject: "",
    categoryId: "",
    priority: "Medium",
    description: "",
  });
  const [ticketErrors, setTicketErrors] = useState({});
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);

  const PRIORITIES = ["High", "Medium", "Low"];

  const FILTER_TABS = [
    { key: "all", label: "All", count: ticketData?.myTickets },
    { key: "Open", label: "Open", count: ticketData?.open },
    { key: "InProgress", label: "In Progress", count: ticketData?.inProgress },
    { key: "Resolved", label: "Resolved", count: ticketData?.resolved },
    { key: "Closed", label: "Closed", count: ticketData?.closed },
  ];

  useEffect(() => {
    fetchTickets();
  }, []);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchTickets = async () => {
    setListLoading(true);
    try {
      const res = await getSupportTicketData();
      if (res.statusCode === 1) setTicketData(res.data);
    } catch (err) {
      console.log("Failed to fetch tickets");
    } finally {
      setListLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await getSupportTicketCategories();
      if (res.statusCode === 1)
        setCategories(res.data.filter((c) => c.isActive));
    } catch (err) {
      console.log("Failed to fetch categories");
    }
  };

  const filteredTickets =
    activeFilter === "all"
      ? ticketData?.tickets || []
      : (ticketData?.tickets || []).filter((t) => t.status === activeFilter);

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.startsWith("0001")) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    if (status === "Open") return { label: "🔵 Open", cls: "badge-info" };
    if (status === "InProgress")
      return { label: "🟡 In Progress", cls: "badge-pending" };
    if (status === "Resolved")
      return { label: "✅ Resolved", cls: "badge-active" };
    if (status === "Closed") return { label: "🔒  Closed", cls: "badge-closed" };
    return { label: status || "—", cls: "badge-gray" };
  };

  const getPriorityBadge = (priority) => {
    if (priority === "High") return { label: "High", cls: "badge-late" };
    if (priority === "Medium") return { label: "Medium", cls: "badge-pending" };
    if (priority === "Low") return { label: "Low", cls: "badge-teal" };
    return { label: priority || "—", cls: "badge-gray" };
  };

  const getPriorityBorder = (priority) => {
    if (priority === "High") return "#dc2626";
    if (priority === "Medium") return "#f59e0b";
    if (priority === "Low") return "#14b8a6";
    return "var(--border-light)";
  };

  const handleTicketFormChange = (e) => {
    const { name, value } = e.target;
    setTicketForm((prev) => ({ ...prev, [name]: value }));
    setTicketErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const openTextModal = () => {
    setShowTypeModal(false);
    setTicketForm({
      subject: "",
      categoryId: "",
      priority: "Medium",
      description: "",
    });
    setTicketErrors({});
    fetchCategories();
    setShowTextModal(true);
  };

  const handleSubmitTicket = async () => {
    const e = {};
    if (!ticketForm.subject) e.subject = "Subject is required.";
    if (!ticketForm.description) e.description = "Description is required.";
    if (Object.keys(e).length) {
      setTicketErrors(e);
      return;
    }

    setSubmitLoading(true);
    try {
      const payload = {
        categoryId: Number(ticketForm.categoryId) || 0,
        subject: ticketForm.subject,
        priority: ticketForm.priority,
        description: ticketForm.description,
        ticketType: "Text",
        audioUrl: "",
      };
      const res = await createSupportTicket(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setShowTextModal(false);
        fetchTickets();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const openTicketDetail = async (ticketId) => {
    try {
      const res = await getSupportTicketById(ticketId);
      if (res.statusCode === 1) {
        setSelectedTicket(res.data);
        setShowDetailModal(true);
        fetchMessages(ticketId);
      }
    } catch (err) {
      toast.error("Failed to load ticket details.");
    }
  };

  const fetchMessages = async (ticketId, silent = false) => {
    if (!silent) setMessagesLoading(true);
    try {
      const res = await getMessagesSupportTicketById(ticketId);
      if (res.statusCode === 1) setMessages(res.data);
    } catch (err) {
      console.log("Failed to fetch messages");
    } finally {
      if (!silent) setMessagesLoading(false);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTicket(null);
    setMessages([]);
    setReplyText("");
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error("Please type a message.");
      return;
    }

    // Optimistic update — turant message dikhao
    const tempMessage = {
      messageId: Date.now(),
      ticketId: selectedTicket.ticketId,
      senderType: "Tenant",
      senderId: selectedTicket.tenantId,
      message: replyText,
      isVoice: false,
      audioUrl: "",
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMessage]);
    const sentText = replyText;
    setReplyText("");

    setReplyLoading(true);
    try {
      const payload = {
        ticketId: selectedTicket.ticketId,
        senderType: "Tenant",
        senderId: selectedTicket.tenantId,
        message: sentText,
        isVoice: false,
        audioUrl: "",
      };
      const res = await replyMessagesSupportTicket(payload);
      if (res.statusCode === 1) {
        // Server se actual messages fetch karo silently
        fetchMessages(selectedTicket.ticketId, true);
      } else {
        toast.error(res.message || "Failed to send message.");
        // Revert karo
        setMessages((prev) =>
          prev.filter((m) => m.messageId !== tempMessage.messageId),
        );
        setReplyText(sentText);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      setMessages((prev) =>
        prev.filter((m) => m.messageId !== tempMessage.messageId),
      );
      setReplyText(sentText);
    } finally {
      setReplyLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr || dateStr.startsWith("0001")) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Support Tickets</div>
            <div className="page-sub">
              Raise and track your complaints &amp; support requests
            </div>
          </div>
          <div className="topbar-right">
            <button
              className="btn btn-primary"
              onClick={() => setShowTypeModal(true)}
            >
              🎫 + New Ticket
            </button>
          </div>
        </div>

        <div className="content">
          {/* Stat Cards */}
          <div className="stat-grid mb16">
            <div className="stat-card">
              <div className="stat-icon">🎫</div>
              <div className="stat-label">My Tickets</div>
              <div className="stat-value">{ticketData?.myTickets ?? 0}</div>
              <div className="stat-delta delta-neu">Total raised</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔵</div>
              <div className="stat-label">Open</div>
              <div className="stat-value">{ticketData?.open ?? 0}</div>
              <div className="stat-delta delta-dn">Awaiting action</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🟡</div>
              <div className="stat-label">In Progress</div>
              <div className="stat-value">{ticketData?.inProgress ?? 0}</div>
              <div className="stat-delta delta-neu">Being resolved</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">Resolved</div>
              <div className="stat-value">{ticketData?.resolved ?? 0}</div>
              <div className="stat-delta delta-up">Completed</div>
            </div>
          </div>

          <div className="card">
            {/* Filter Tabs */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {FILTER_TABS.map((tab) => {
                  const isActive = activeFilter === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveFilter(tab.key)}
                      style={{
                        padding: "6px 14px",
                        fontSize: 11,
                        fontWeight: isActive ? 700 : 500,
                        border: `1px solid ${isActive ? "var(--teal)" : "var(--border-mid)"}`,
                        borderRadius: 99,
                        cursor: "pointer",
                        background: isActive
                          ? "var(--teal)"
                          : "var(--bg-primary)",
                        color: isActive ? "#fff" : "var(--text-secondary)",
                      }}
                    >
                      {tab.label}
                      {tab.count !== null && tab.count !== undefined && (
                        <span
                          style={{
                            background: isActive
                              ? "rgba(255,255,255,0.25)"
                              : "var(--border-light)",
                            borderRadius: 99,
                            padding: "1px 7px",
                            fontSize: 10,
                            marginLeft: 5,
                          }}
                        >
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Ticket List */}
            <div>
              {listLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      border: "0.5px solid var(--border-light)",
                      borderRadius: "var(--radius-md)",
                      padding: 14,
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {Array.from({ length: 3 }).map((_, j) => (
                        <div
                          key={j}
                          style={{
                            height: 14,
                            borderRadius: 6,
                            background:
                              "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                            backgroundSize: "200% 100%",
                            animation: "shimmer 1.4s infinite",
                            width: j === 0 ? "30%" : j === 1 ? "60%" : "50%",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ))
              ) : filteredTickets.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "var(--text-tertiary)",
                    padding: 32,
                    fontSize: 13,
                  }}
                >
                  No tickets found.
                </div>
              ) : (
                filteredTickets.map((ticket) => {
                  const statusBadge = getStatusBadge(ticket.status);
                  const priorityBadge = getPriorityBadge(ticket.priority);
                  return (
                    <div
                      key={ticket.ticketId}
                      onClick={() => openTicketDetail(ticket.ticketId)}
                      style={{
                        borderLeft: `4px solid ${getPriorityBorder(ticket.priority)}`,
                        border: "0.5px solid var(--border-light)",
                        borderRadius: "var(--radius-md)",
                        padding: 14,
                        marginBottom: 10,
                        background: "var(--bg-primary)",
                        cursor: "pointer",
                      }}
                    >
                      {/* Meta */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexWrap: "wrap",
                          marginBottom: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                            fontFamily: "monospace",
                          }}
                        >
                          {ticket.ticketNumber}
                        </span>
                        <span
                          className={`badge ${statusBadge.cls}`}
                          style={{ fontSize: 11, fontWeight: 700 }}
                        >
                          {statusBadge.label}
                        </span>
                        <span
                          className={`badge ${priorityBadge.cls}`}
                          style={{ fontSize: 10, fontWeight: 700 }}
                        >
                          {priorityBadge.label}
                        </span>
                        {ticket.categoryIcon && ticket.categoryName && (
                          <span
                            className="badge badge-gray"
                            style={{ fontSize: 10 }}
                          >
                            {ticket.categoryIcon} {ticket.categoryName}
                          </span>
                        )}
                        {ticket.ticketType === "Voice" && (
                          <span
                            className="badge badge-voice"
                            style={{ fontSize: 10 }}
                          >
                            🎤 Voice
                          </span>
                        )}
                      </div>

                      {/* Subject */}
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          marginBottom: 4,
                          color: "var(--text-primary)",
                        }}
                      >
                        {ticket.subject || "—"}
                      </div>

                      {/* Description */}
                      {ticket.description && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--text-secondary)",
                            marginTop: 4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ticket.description}
                        </div>
                      )}

                      {/* Footer */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginTop: 10,
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            color: "var(--text-tertiary)",
                          }}
                        >
                          {formatDate(ticket.createdAt)}
                          {ticket.messageCount > 0 && (
                            <>&nbsp;·&nbsp;💬 {ticket.messageCount} msg</>
                          )}
                        </span>
                        <button
                          className="btn btn-xs btn-primary"
                          onClick={(e) => {
                            openTicketDetail(ticket.ticketId);
                          }}
                        >
                          💬 Reply
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Type Selection Modal ── */}
      {showTypeModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            style={{
              background: "var(--bg-primary)",
              borderRadius: "var(--radius-lg)",
              width: "100%",
              maxWidth: 480,
              boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
            }}
          >
            <div style={{ padding: "20px 22px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🎫</div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                New Support Ticket
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--text-secondary)",
                  marginTop: 4,
                }}
              >
                How would you like to create your ticket?
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 14,
                padding: "0 22px 22px",
                flexWrap: "wrap",
              }}
            >
              <div
                onClick={openTextModal}
                style={{
                  flex: "1 1 180px",
                  border: "0.5px solid var(--border-light)",
                  borderRadius: "var(--radius-md)",
                  padding: 20,
                  cursor: "pointer",
                  textAlign: "center",
                  background: "var(--bg-secondary)",
                }}
              >
                <div style={{ fontSize: 36, marginBottom: 10 }}>✍️</div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--text-primary)",
                    marginBottom: 4,
                  }}
                >
                  Text Ticket
                </div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  Fill a form with subject, category and description
                </div>
              </div>
              <div
                onClick={() => setShowTypeModal(false)}
                style={{
                  flex: "1 1 180px",
                  border: "0.5px solid var(--border-light)",
                  borderRadius: "var(--radius-md)",
                  padding: 20,
                  cursor: "pointer",
                  textAlign: "center",
                  background: "var(--bg-secondary)",
                }}
              >
                <div style={{ fontSize: 36, marginBottom: 10 }}>🎤</div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--text-primary)",
                    marginBottom: 4,
                  }}
                >
                  Voice Ticket
                </div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  Speak your complaint — AI will process it
                </div>
              </div>
            </div>
            <div style={{ padding: "0 22px 18px", textAlign: "center" }}>
              <button
                onClick={() => setShowTypeModal(false)}
                style={{
                  border: "none",
                  background: "none",
                  color: "var(--text-secondary)",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                ✕ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Text Ticket Modal ── */}
      {showTextModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            style={{
              background: "var(--bg-primary)",
              borderRadius: "var(--radius-lg)",
              width: "100%",
              maxWidth: 560,
              maxHeight: "94vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid var(--border-light)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  ✍️ Create Text Ticket
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--text-secondary)",
                    marginTop: 2,
                  }}
                >
                  Describe your issue in detail
                </div>
              </div>
              <button
                onClick={() => setShowTextModal(false)}
                style={{
                  border: "none",
                  background: "none",
                  fontSize: 24,
                  cursor: "pointer",
                  color: "var(--text-tertiary)",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ overflowY: "auto", flex: 1, padding: 20 }}>
              <div className="form-group mb14">
                <label>Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={ticketForm.subject}
                  onChange={handleTicketFormChange}
                  placeholder="e.g. Water leakage in bathroom"
                  style={ticketErrors.subject ? { borderColor: "#dc2626" } : {}}
                />
                {ticketErrors.subject && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {ticketErrors.subject}
                  </span>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div className="form-group">
                  <label>Category</label>
                  <select
                    name="categoryId"
                    value={ticketForm.categoryId}
                    onChange={handleTicketFormChange}
                  >
                    <option value="">Select Category</option>
                    {categories.map((c) => (
                      <option key={c.categoryId} value={c.categoryId}>
                        {c.icon} {c.categoryName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      paddingTop: 4,
                      flexWrap: "wrap",
                    }}
                  >
                    {PRIORITIES.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() =>
                          setTicketForm((prev) => ({ ...prev, priority: p }))
                        }
                        style={{
                          padding: "5px 12px",
                          fontSize: 11,
                          borderRadius: 99,
                          cursor: "pointer",
                          fontWeight: ticketForm.priority === p ? 700 : 400,
                          border: `1px solid ${ticketForm.priority === p ? "var(--blue)" : "var(--border-mid)"}`,
                          background:
                            ticketForm.priority === p
                              ? "var(--blue)"
                              : "var(--bg-primary)",
                          color:
                            ticketForm.priority === p
                              ? "#fff"
                              : "var(--text-secondary)",
                        }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group mb14">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={ticketForm.description}
                  onChange={handleTicketFormChange}
                  rows={4}
                  placeholder="Describe the issue in detail…"
                  style={
                    ticketErrors.description ? { borderColor: "#dc2626" } : {}
                  }
                />
                {ticketErrors.description && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {ticketErrors.description}
                  </span>
                )}
              </div>
            </div>

            <div className="modal-footer" style={{ flexShrink: 0 }}>
              <button
                className="btn"
                onClick={() => setShowTextModal(false)}
                disabled={submitLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmitTicket}
                disabled={submitLoading}
              >
                {submitLoading ? "Submitting..." : "📤 Submit Ticket"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && selectedTicket && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              background: "var(--bg-primary)",
              borderRadius: "var(--radius-lg)",
              width: "100%",
              maxWidth: 700,
              maxHeight: "94vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
            }}
          >
            {/* Header — same as before */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid var(--border-light)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "var(--text-tertiary)",
                    }}
                  >
                    {selectedTicket.ticketNumber}
                  </span>
                  <span
                    className={`badge ${getStatusBadge(selectedTicket.status).cls}`}
                    style={{ fontSize: 11, fontWeight: 700 }}
                  >
                    {getStatusBadge(selectedTicket.status).label}
                  </span>
                  <span
                    className={`badge ${getPriorityBadge(selectedTicket.priority).cls}`}
                    style={{ fontSize: 10, fontWeight: 700 }}
                  >
                    {getPriorityBadge(selectedTicket.priority).label}
                  </span>
                  {selectedTicket.categoryIcon &&
                    selectedTicket.categoryName && (
                      <span
                        className="badge badge-gray"
                        style={{ fontSize: 10 }}
                      >
                        {selectedTicket.categoryIcon}{" "}
                        {selectedTicket.categoryName}
                      </span>
                    )}
                  {selectedTicket.ticketType === "Voice" && (
                    <span
                      className="badge badge-voice"
                      style={{ fontSize: 10 }}
                    >
                      🎤 Voice
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {selectedTicket.subject}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--text-tertiary)",
                    marginTop: 3,
                  }}
                >
                  Created: {formatDateTime(selectedTicket.createdAt)}
                </div>
              </div>
              <button
                onClick={closeDetailModal}
                style={{
                  border: "none",
                  background: "none",
                  fontSize: 24,
                  cursor: "pointer",
                  color: "var(--text-tertiary)",
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>

            {/* ── PART 1: Description — FIXED, scroll nahi hoga ── */}
            <div style={{ padding: "18px 20px 0", flexShrink: 0 }}>
              <div
                className="info-box"
                style={{ marginBottom: 14, fontSize: 12, lineHeight: "1.6" }}
              >
                <strong>📋 Description:</strong> {selectedTicket.description}
              </div>

              {selectedTicket.ticketType === "Voice" &&
                selectedTicket.audioUrl && (
                  <div
                    style={{
                      background: "var(--purple-light)",
                      borderRadius: "var(--radius-md)",
                      padding: "12px 14px",
                      marginBottom: 14,
                      border: "0.5px solid #C4B5FD",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "var(--purple)",
                        marginBottom: 6,
                      }}
                    >
                      🎤 Voice Ticket — AI Transcript
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#3C3489",
                        lineHeight: "1.6",
                      }}
                    >
                      {selectedTicket.description}
                    </div>
                  </div>
                )}

              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text-tertiary)",
                  textTransform: "uppercase",
                  letterSpacing: ".06em",
                  marginBottom: 8,
                }}
              >
                Conversation
              </div>
            </div>

            {/* ── PART 2: Chat messages — SCROLLABLE, ref yahan ── */}
            <div
              ref={chatContainerRef}
              style={{ overflowY: "auto", flex: 1, padding: "0 20px 18px" }}
            >
              {messagesLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        height: 14,
                        borderRadius: 6,
                        background:
                          "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                        width: "60%",
                      }}
                    />
                  </div>
                ))
              ) : messages.length === 0 ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-tertiary)",
                    padding: "12px 0",
                  }}
                >
                  No messages yet.
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  {messages.map((msg) => {
                    const isTenant = msg.senderType === "Tenant";
                    return (
                      <div
                        key={msg.messageId}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: isTenant ? "flex-end" : "flex-start",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            color: "var(--text-tertiary)",
                            marginBottom: 3,
                            textAlign: isTenant ? "right" : "left",
                          }}
                        >
                          {msg.senderType}
                        </div>
                        <div
                          style={{
                            maxWidth: "75%",
                            padding: "10px 14px",
                            borderRadius: 12,
                            fontSize: 13,
                            lineHeight: "1.5",
                            background: isTenant
                              ? "var(--blue)"
                              : "var(--bg-secondary)",
                            color: isTenant ? "#fff" : "var(--text-primary)",
                            borderBottomRightRadius: isTenant ? 4 : 12,
                            borderBottomLeftRadius: isTenant ? 12 : 4,
                          }}
                        >
                          {msg.message}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "var(--text-tertiary)",
                            marginTop: 3,
                            textAlign: isTenant ? "right" : "left",
                          }}
                        >
                          {formatDateTime(msg.createdAt)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Reply Box — same as before */}
            <div
              style={{
                padding: "14px 20px",
                borderTop: "1px solid var(--border-light)",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <textarea
                  rows={2}
                  placeholder="Type your reply here…"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  style={{
                    flex: 1,
                    resize: "none",
                    fontSize: 13,
                    padding: "10px 12px",
                    border: "1px solid var(--border-mid)",
                    borderRadius: "var(--radius-md)",
                  }}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleSendReply}
                  disabled={replyLoading}
                  style={{ flexShrink: 0, padding: "10px 16px" }}
                >
                  {replyLoading ? "..." : "Send 📤"}
                </button>
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--text-tertiary)",
                  marginTop: 5,
                }}
              >
                Press Send to post your reply. Owner will be notified.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
